import { useCallback, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import PQueue from 'p-queue';
import {
  useCreateNCBIConcatenatedFastqgz,
  useTriggerAnalysis
} from '../services/api/KeynomeAPIMutations';
import ApiMethods from '../services/api/ApiMethods';
import { fetchSampleAnalysisStatus } from '../services/api/analysisUtils';

function buildError(code, message, extra = {}) {
  const error = new Error(message || code);
  error.code = code;
  Object.assign(error, extra);
  return error;
}

function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(buildError('FILE_READ_ERROR', 'Failed to read uploaded file.'));
    reader.readAsText(file);
  });
}

function parseIdentifiers(content) {
  return Array.from(
    new Set(
      content
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean)
    )
  );
}

const NCBI_ID_REGEX = /^(SRR|ERR|SRS)\d+$/;
const ANALYSIS_SKIP_STATUSES = new Set(['analyzing', 'succeeded', 'failed', 'delayed']);

function getResponseErrorReason(response, fallback) {
  const detail = response?.data?.errors?.[0]?.detail;
  const title = response?.data?.errors?.[0]?.title;
  return detail || title || fallback;
}

export function useNCBIUpload({ projectId, onSuccess, onError }) {
  const { getAccessTokenSilently } = useAuth0();
  const { mutateAsync: createNCBIConcatenatedFastqgz } = useCreateNCBIConcatenatedFastqgz();
  const { mutateAsync: triggerAnalysis } = useTriggerAnalysis();

  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState({ detail: '' });
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStartTime, setUploadStartTime] = useState(null);
  const [completedCount, setCompletedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [results, setResults] = useState(null);

  const upload = useCallback(async (file) => {
    if (!projectId) {
      onError?.(buildError('PROJECT_REQUIRED', 'Select a project before uploading.'));
      return;
    }

    setIsUploading(true);
    setProgress(0);
    setStatus({ detail: 'Reading SRR file...' });
    setUploadStartTime(Date.now());
    setCompletedCount(0);
    setTotalCount(0);
    setResults(null);

    try {
      const content = await readFileAsText(file);
      const identifiers = parseIdentifiers(content);

      if (!identifiers.length) {
        throw buildError('EMPTY_FILE', 'No SRR/ERR identifiers were found in the file.');
      }

      const validationErrors = [];
      const validIdentifiers = [];
      identifiers.forEach((identifier) => {
        if (NCBI_ID_REGEX.test(identifier)) {
          validIdentifiers.push(identifier);
        } else {
          validationErrors.push({
            srr: identifier,
            reason: 'Identifier format must match SRR#### or ERR####.'
          });
        }
      });

      setTotalCount(identifiers.length);
      setCompletedCount(validationErrors.length);
      setProgress(Math.round((validationErrors.length / identifiers.length) * 100));
      setStatus({ detail: 'Submitting identifiers to NCBI...' });
      const accessToken = await getAccessTokenSilently();

      const queue = new PQueue({ concurrency: 2 });
      let localCompletedCount = validationErrors.length;
      let createdCount = 0;
      let existingCount = 0;
      const errors = [...validationErrors];

      const tasks = validIdentifiers.map((identifier) => queue.add(async () => {
        setStatus({ label: `Processing ${identifier}`, detail: 'creating sequencing records...' });

        try {
          const response = await ApiMethods.requestWithRetry(
            async () => {
              const uploadResponse = await createNCBIConcatenatedFastqgz({
                projectId,
                ncbiSrr: identifier
              });

              if (uploadResponse?.status === 502) {
                throw buildError(
                  'NCBI_RATE_LIMITED',
                  getResponseErrorReason(uploadResponse, 'NCBI temporarily unavailable.'),
                  { status: 502, identifier }
                );
              }

              return uploadResponse;
            },
            { maxRetries: 3, delay: 2000 }
          );

          if (response?.status === 201) {
            createdCount += 1;
            const fastqId = response?.data?.id;
            if (!fastqId) {
              errors.push({
                srr: identifier,
                reason: 'Created NCBI record is missing FASTQ identifier.'
              });
            } else {
              await triggerAnalysis({ fastqId });
            }
          } else if (response?.status === 200) {
            existingCount += 1;
            const sampleId = response?.data?.attributes?.lib_seq_id;
            if (!sampleId) {
              errors.push({
                srr: identifier,
                reason: 'Existing NCBI record is missing sample relationship.'
              });
            } else {
              const analysisStatus = await fetchSampleAnalysisStatus({
                sampleId,
                accessToken
              });
              if (!ANALYSIS_SKIP_STATUSES.has(analysisStatus)) {
                const fastqId = response?.data?.id;
                if (!fastqId) {
                  errors.push({
                    srr: identifier,
                    reason: 'Existing NCBI record is missing FASTQ identifier.'
                  });
                } else {
                  await triggerAnalysis({ fastqId });
                }
              }
            }
          } else if (response?.status === 422) {
            errors.push({
              srr: identifier,
              reason: getResponseErrorReason(response, 'Invalid SRR/ERR identifier.')
            });
          } else if (response?.status !== 201 && response?.status !== 200) {
            errors.push({
              srr: identifier,
              reason: getResponseErrorReason(response, `Request failed with status ${response?.status || 'unknown'}.`)
            });
          }
        } catch (error) {
          const isRateLimitError = error?.code === 'NCBI_RATE_LIMITED';
          errors.push({
            srr: identifier,
            reason: isRateLimitError
              ? 'NCBI temporarily unavailable after 3 retries (2s, 4s, 8s).'
              : (error?.message || 'Failed to submit identifier.')
          });
        }

        localCompletedCount += 1;
        setCompletedCount(localCompletedCount);
        setProgress(Math.round((localCompletedCount / identifiers.length) * 100));
      }));

      await Promise.allSettled(tasks);

      setStatus({ detail: 'Finalizing...' });
      setProgress(100);
      const uploadResults = {
        createdCount,
        existingCount,
        totalCount: identifiers.length,
        errors
      };

      setResults(uploadResults);
      onSuccess?.(uploadResults);
    } catch (error) {
      setStatus({ detail: '' });
      setProgress(0);
      onError?.(error);
    } finally {
      setIsUploading(false);
      setUploadStartTime(null);
    }
  }, [
    createNCBIConcatenatedFastqgz,
    getAccessTokenSilently,
    onError,
    onSuccess,
    projectId,
    triggerAnalysis
  ]);

  const clearResults = useCallback(() => {
    setResults(null);
  }, []);

  return {
    upload,
    progress,
    status,
    isUploading,
    uploadStartTime,
    completedCount,
    totalCount,
    results,
    clearResults
  };
}

export default useNCBIUpload;
